import { defineStore } from 'pinia';
import { MeApi, type Me, type Org } from '@/api/me';
import { AuthApi } from '@/api/auth';
import { ApiError } from '@/api/links';

// inbox-103 — useMeStore: текущий user + organization для всего cabinet.
// Загружается один раз на mount AppCabinet, потом кэшируется.
// На 401 — clear + redirect на /login (handled в caller).
// inbox-138 — добавлен `orgs` + `loadOrgs()` для org-switcher dropdown.

interface State {
  me: Me | null;
  orgs: Org[];
  /** True пока loadOrgs не был сделан хотя бы раз. */
  orgsInitialized: boolean;
  loading: boolean;
  error: string | null;
  /** True если запрос был сделан хотя бы раз (даже неуспешный) — чтобы не дёргать /me на каждый mount. */
  initialized: boolean;
}

export const useMeStore = defineStore('me', {
  state: (): State => ({
    me: null,
    orgs: [],
    orgsInitialized: false,
    loading: false,
    error: null,
    initialized: false,
  }),
  getters: {
    isAuthenticated: (s) => s.me !== null,
    // inbox-268 — staff_role 'support' / 'admin' / 'superadmin' даёт доступ
    // к /console/*. 'none' (default) или отсутствие поля = deny.
    isStaff: (s) => {
      const r = s.me?.staff_role;
      return r === 'support' || r === 'admin' || r === 'superadmin';
    },
    // inbox-257 — effective plan: user_plan если есть, иначе organization_plan
    // (legacy fallback). UI читает этот getter для gating Pro-фич.
    effectivePlan: (s): string => {
      if (!s.me) return 'free';
      return s.me.user_plan || s.me.organization_plan || 'free';
    },
    // Pro+ tier — starter / business / team / enterprise. Free → деgrade.
    isPro: (s): boolean => {
      const plan = s.me?.user_plan || s.me?.organization_plan || 'free';
      return plan === 'starter' || plan === 'business' || plan === 'team' || plan === 'enterprise';
    },
    currentUser: (s) => s.me,
    currentOrg: (s) => (s.me ? { id: s.me.organization_id, name: s.me.organization_name, plan: s.me.organization_plan } : null),
    planLabel: (s) => {
      if (!s.me) return '';
      const labels: Record<string, string> = {
        free: 'Бесплатно', starter: 'Старт', business: 'Бизнес', team: 'Команда', enterprise: 'Энтерпрайз',
      };
      return labels[s.me.organization_plan] || s.me.organization_plan;
    },
    orgInitials: (s) => {
      if (!s.me) return '?';
      const name = s.me.organization_name || '';
      const words = name.replace(/[«»"']/g, '').split(/\s+/).filter(Boolean);
      if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
      if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
      return '?';
    },
  },
  actions: {
    async load(force = false): Promise<Me | null> {
      if (this.initialized && !force && this.me) return this.me;
      this.loading = true;
      this.error = null;
      try {
        this.me = await MeApi.get();
        this.initialized = true;
        return this.me;
      } catch (e) {
        this.me = null;
        if (e instanceof ApiError && e.status === 401) {
          this.error = 'unauthorized';
        } else {
          this.error = (e as Error).message;
        }
        this.initialized = true;
        return null;
      } finally {
        this.loading = false;
      }
    },
    /** inbox-138 — загрузить список org'ов для org-switcher dropdown. */
    async loadOrgs(force = false): Promise<Org[]> {
      if (this.orgsInitialized && !force) return this.orgs;
      try {
        this.orgs = await MeApi.getOrgs();
        this.orgsInitialized = true;
        return this.orgs;
      } catch (e) {
        if (!(e instanceof ApiError && e.status === 401)) {
          // 401 ⇒ caller route guards переведут на /login; остальные —
          // сохраняем в error, но orgs остаётся пустым массивом.
          this.error = (e as Error).message;
        }
        this.orgs = [];
        this.orgsInitialized = true;
        return [];
      }
    },
    async logout(): Promise<void> {
      try { await AuthApi.logout(); } catch { /* fail-quiet */ }
      this.me = null;
      this.orgs = [];
      this.orgsInitialized = false;
      this.initialized = false;
    },
    clear(): void {
      this.me = null;
      this.orgs = [];
      this.orgsInitialized = false;
      this.initialized = false;
      this.error = null;
    },
  },
});
